<template>
  <div>
    <form-section :title="$t('Integrations_Settings_BasicSettings')">
      <rest-form-item
        :label="$t('Integrations_Field_Name')"
        :hint="$t('Integrations_Field_Name_Description')"
        prop="name"
      >
        <el-input
          v-model="model.name"
          :placeholder="$t('Integrations_Field_Name')"
        />
      </rest-form-item>
    </form-section>

    <form-section :title="$t('Integrations_Settings_ConnectionSettings')">
      <rest-form-item
        :label="$t('Integrations_Optimove_Auth_Type')"
        prop="params.auth_type"
      >
        <enum-select
          :number="true"
          v-model="model.params.auth_type"
          :options="OptimoveAuthenticationTypesLabels"
        />
      </rest-form-item>

      <rest-form-item
        :label="$t('Integrations_Optimove_Field_Api')"
        prop="params.auth_key"
        v-if="model.params.auth_type === OptimoveAuthenticationTypes.api"
      >
        <el-input
          v-model="model.params.auth_key"
          :placeholder="$t('Integrations_Optimove_Field_Api')"
        />
      </rest-form-item>
      <rest-form-item
        :label="$t('Integrations_Optimove_Field_Username')"
        prop="params.username"
        v-if="model.params.auth_type === OptimoveAuthenticationTypes.username"
      >
        <el-input
          v-model="model.params.username"
          :placeholder="$t('Integrations_Optimove_Field_Username')"
        />
      </rest-form-item>

      <rest-form-item
        :label="$t('Integrations_Optimove_Field_Password')"
        prop="params.password"
        v-if="model.params.auth_type === OptimoveAuthenticationTypes.username"
      >
        <el-input
          v-model="model.params.password"
          :placeholder="$t('Integrations_Optimove_Field_Password')"
        />
      </rest-form-item>

      <rest-form-item
        :label="$t('Integrations_Optimove_Field_Endpoint')"
        prop="params.endpoint"
      >
        <el-input
          v-model="model.params.endpoint"
          :placeholder="$t('Integrations_Optimove_Field_Endpoint')"
        />
      </rest-form-item>
      <rest-form-item
        :label="$t('Integrations_Optimove_Field_SyncAttributes')"
        :description="$t('Integrations_Optimove_Field_SyncAttributes_Desc')"
        prop="params.sync_attributes"
      >
        <feature-toggle v-model="model.params.sync_attributes" />
      </rest-form-item>
    </form-section>

    <form-section :title="$t('Integrations_Settings_MatchingSettings')">
      <rest-form-item
        prop="params.id_attribute"
        :label="$t('Integrations_Optimove_Field_IdAttribute')"
        :hint="$t('Integrations_Optimove_Field_IdAttribute_Description')"
      >
        <el-input
          v-model="model.params.id_attribute"
          :placeholder="
            $t('Integrations_Optimove_Field_IdAttribute_Placeholder')
          "
        />
      </rest-form-item>

      <rest-form-item
        prop="params.mobile_number_attribute"
        :label="$t('Integrations_Optimove_Field_MobileNumberAttribute')"
        :hint="
          $t('Integrations_Optimove_Field_MobileNumberAttribute_Description')
        "
      >
        <el-input
          v-model="model.params.mobile_number_attribute"
          :placeholder="
            $t('Integrations_Optimove_Field_MobileNumberAttribute_Placeholder')
          "
        />
      </rest-form-item>
    </form-section>

    <form-section :title="$t('Integrations_Settings_SuppressionAttributeSync')">
      <rest-form-item
        prop="params.custom_attributes"
        :label="$t('Integrations_Optimove_Field_CustomAttributes')"
        :hint="$t('Integrations_Optimove_Field_CustomAttributes_Description')"
      >
        <el-select
          v-model="model.params.custom_attributes"
          filterable
          remote
          :remote-method="fetchAttributesRemote"
          :multiple="true"
          :loading="selectLoading"
          :placeholder="
            $t('Integrations_Optimove_Field_CustomAttributes_Placeholder')
          "
        >
          <el-option
            v-for="item in customAttributes"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </rest-form-item>
    </form-section>

    <form-section
      :title="$t('Integrations_Settings_Events')"
      :description="$t('Integrations_Settings_Events_Description')"
      v-if="false"
    >
      <events-list-draggable v-model="model.params.events" />
    </form-section>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import EventsListDraggable from '@/modules/events/components/EventsListDraggable'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import {
  OptimoveAuthenticationTypes,
  OptimoveAuthenticationTypesLabels
} from '@/modules/integrations/enum/AuthenticationTypesEnum'
import EnumSelect from '@/common/components/form/EnumSelect'
import FeatureToggle from '@/common/components/form/FeatureToggle'

export default {
  name: 'OptimoveSettings',

  props: {
    model: {
      type: Object,
      required: true
    }
  },

  components: {
    EnumSelect,
    EventsListDraggable,
    FormSection,
    RestFormItem,
    FeatureToggle
  },

  created() {
    if (!this.model.params.hasOwnProperty('auth_type')) {
      if (this.model.params.username) {
        this.$set(
          this.model.params,
          'auth_type',
          OptimoveAuthenticationTypes.username
        )
      } else {
        this.$set(
          this.model.params,
          'auth_type',
          OptimoveAuthenticationTypes.api
        )
      }
    }
    this.fetchAttributes()
  },

  computed: {
    attributesEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `tags`,
        params: { sort: 'title', attribute_profile: 1 }
      })
    }
  },

  methods: {
    fetchAttributes() {
      this.selectLoading = true
      RestApi(this.attributesEndpoint)
        .then(({ data }) => {
          this.customAttributes = data
          this.filterAttributes()
          this.selectLoading = false
        })
        .catch(error => {
          this.selectLoading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    fetchAttributesRemote(query) {
      this.search = query

      let params = { sort: 'title', attribute_profile: 1 }
      if (query !== null && query !== '') {
        params.title = query
      }

      RestApi({
        ...this.attributesEndpoint,
        params: params
      })
        .then(({ data }) => {
          this.customAttributes = data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    filterAttributes() {
      if (
        !this.model.params.custom_attributes ||
        !this.model.params.custom_attributes.length
      ) {
        return
      }

      this.model.params.custom_attributes = this.model.params.custom_attributes.filter(
        opt => this.customAttributes.map(el => el.id).includes(opt)
      )
    }
  },

  data() {
    return {
      selectLoading: false,
      customAttributes: [],
      rules: {
        name: {
          required: true,
          message: this.$t('RestForm_FieldIsRequired')
        },
        params: {
          username: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          password: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          },
          endpoint: {
            required: true,
            message: this.$t('RestForm_FieldIsRequired')
          }
        }
      },
      OptimoveAuthenticationTypes,
      OptimoveAuthenticationTypesLabels
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/integrations/types/optimove/OptimoveSettings.vue